import Field from './Field';
import ArtifactCard from './ArtifactCard';
import { humanize } from '@/lib/humanize';
import { getArtifactTypes } from '@/lib/filters';

function formatDoiUrl(doiOrUrl) {
  if (!doiOrUrl) return null;
  if (doiOrUrl.startsWith('http://') || doiOrUrl.startsWith('https://')) return doiOrUrl;
  return `https://doi.org/${doiOrUrl}`;
}

const RELATIONSHIP_LABELS = {
  'shared-keywords': 'Shared Keywords',
};

/**
 * Full rendering of a paper's schema-nested record. Shared between the
 * full-page detail view and the slide-over panel used from the graph, so
 * both stay in sync.
 */
export default function PaperDetailContent({ paper, artifactConfig, relatedPapers = [], onRelatedClick, onRemove }) {
  const { metadata, contribution } = paper;
  const focus = contribution.focus_intent || {};
  const comp = contribution.research_components || {};
  const rq = focus.research_question || {};
  const artifacts = contribution.artifacts || [];
  const hasFocusIntent = Boolean(focus.goal_paper || rq.question || focus.motivation_paper || focus.outcome_paper);

  return (
    <>
      {paper._userAdded && (
        <div className="user-added-banner">
          <span>+ Added by you — this paper lives only in your browser.</span>
          {onRemove && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onRemove}>
              Remove
            </button>
          )}
        </div>
      )}
      <div className="paper-detail__badges">
        {getArtifactTypes(paper).map((t) => (
          <span
            key={t}
            className="paper-card__badge"
            style={{ '--inquiry-color': artifactConfig[t]?.color || '#88c0d0' }}
          >
            {artifactConfig[t]?.label || humanize(t)}
          </span>
        ))}
      </div>

      <h1 className="paper-detail__title">{metadata.title || 'Anonymized paper'}</h1>
      <p className="paper-detail__meta">
        {[metadata.authors?.join(', '), metadata.year, metadata.track].filter(Boolean).join(' · ')}
      </p>
      {metadata.title && metadata.doi_or_url && (
        <a
          className="btn btn--secondary btn--sm"
          href={formatDoiUrl(metadata.doi_or_url)}
          target="_blank"
          rel="noopener noreferrer"
        >
          View Paper →
        </a>
      )}

      <Field label={humanize('keywords')} value={contribution.keywords} />

      {hasFocusIntent && (
        <section className="detail-section">
          <h2 className="detail-section__title">Focus and Intent</h2>
          <Field label={humanize('goal_paper')} value={focus.goal_paper} />
          {rq.question && (
            <>
              <Field label={humanize('question')} value={rq.question} />
              <Field label={humanize('hidden_in_text')} value={rq.hidden_in_text} />
              <Field label={humanize('highlighted_in_text')} value={rq.highlighted_in_text} />
            </>
          )}
          <Field label={humanize('motivation_paper')} value={focus.motivation_paper} />
          <Field label={humanize('outcome_paper')} value={focus.outcome_paper} />
        </section>
      )}

      <section className="detail-section">
        <h2 className="detail-section__title">Research Components</h2>
        <Field label={humanize('type_of_theory')} value={comp.type_of_theory} />
        <Field label={humanize('emphasis')} value={comp.emphasis} />
        <Field label={humanize('formal_concepts')} value={comp.formal_concepts} />
        <Field label={humanize('algorithms')} value={comp.algorithms} />
        <Field label={humanize('hypothesis')} value={comp.hypothesis} />
        <Field label={humanize('independent_variables')} value={comp.independent_variables} />
        <Field label={humanize('dependent_variables')} value={comp.dependent_variables} />
      </section>

      {artifacts.length > 0 && (
        <section className="detail-section">
          <h2 className="detail-section__title">{humanize('artifacts')}</h2>
          <div className="artifact-grid">
            {artifacts.map((artifact, i) => (
              <ArtifactCard key={i} artifact={artifact} index={i} />
            ))}
          </div>
        </section>
      )}

      {relatedPapers.length > 0 && (
        <section className="detail-section">
          <h2 className="detail-section__title">Related Entries</h2>
          <div className="related-papers">
            {relatedPapers.map((related) => (
              <button
                key={related._id}
                type="button"
                className="related-paper"
                onClick={() => onRelatedClick?.(related._id)}
              >
                <span className="related-paper__title">{related.metadata.title || 'Anonymized paper'}</span>
                <span className="related-paper__relationship">{RELATIONSHIP_LABELS[related.relationship]}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
